import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getSecurePDFUrl } from '../utils/pdfStorage';

function SecurePDFViewer({ pdfFileName, onLoadingChange }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState([]);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const activeRenderTasks = useRef(new Map()); // Track active render tasks

  useEffect(() => {
    // Prevent right-click
    const handleContextMenu = (e) => e.preventDefault();
    // Prevent text selection
    const handleSelectStart = (e) => e.preventDefault();
    // Prevent drag
    const handleDragStart = (e) => e.preventDefault();
    // Prevent print
    const handleBeforePrint = () => {
      document.body.style.display = 'none';
    };
    const handleAfterPrint = () => {
      document.body.style.display = 'block';
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  useEffect(() => {
    if (onLoadingChange) onLoadingChange(true);
    async function loadPDF() {
      if (!user) {
        setError('Please log in to view this content');
        setLoading(false);
        if (onLoadingChange) onLoadingChange(false);
        return;
      }

      try {
        // Get secure URL for the PDF
        const secureUrl = await getSecurePDFUrl(pdfFileName);
        // Check if PDF.js is loaded
        if (!window['pdfjs-dist/build/pdf']) {
          //console.error('PDF.js library not loaded');
          setError('PDF viewer not properly initialized. Please refresh the page.');
          setLoading(false);
          if (onLoadingChange) onLoadingChange(false);
          return;
        }

        // Load PDF using PDF.js
        const pdfjsLib = window['pdfjs-dist/build/pdf'];
        //console.log('Loading PDF from secure URL');
        
        // Set worker source
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
        
        try {
          const loadingTask = pdfjsLib.getDocument({
            url: secureUrl,
            verbosity: 0, // Reduce console noise
            isEvalSupported: false, // Security enhancement
            disableFontFace: false, // Keep font rendering
            disableRange: false, // Allow range requests
            disableStream: false, // Allow streaming
            disableAutoFetch: false, // Allow auto-fetch of missing data
            maxImageSize: 1024 * 1024 * 10 // 10MB max image size
          });
          
          console.log('PDF loading task created');
          
          const pdf = await loadingTask.promise;
          console.log('PDF loaded successfully, number of pages:', pdf.numPages);
          
          // Load pages sequentially to avoid overwhelming the browser
          const pdfPages = [];
          for (let i = 1; i <= pdf.numPages; i++) {
            try {
              const page = await pdf.getPage(i);
              pdfPages.push(page);
              console.log(`Page ${i} loaded successfully`);
            } catch (pageError) {
              console.error(`Error loading page ${i}:`, pageError);
              // Create a placeholder for failed pages
              pdfPages.push(null);
            }
          }

          console.log('All PDF pages processed');
          setPages(pdfPages.filter(page => page !== null)); // Only keep successfully loaded pages
          setLoading(false);
          if (onLoadingChange) onLoadingChange(false);
        } catch (pdfError) {
          console.error('Error in PDF loading process:', pdfError);
          setError(`Error loading PDF: ${pdfError.message}`);
          setLoading(false);
          if (onLoadingChange) onLoadingChange(false);
        }
      } catch (err) {
        //console.error('Error in loadPDF:', err);
        setError(`Error: ${err.message}`);
        setLoading(false);
        if (onLoadingChange) onLoadingChange(false);
      }
    }

    loadPDF();
  }, [pdfFileName, user, onLoadingChange]);

  // Render PDF pages
  useEffect(() => {
    if (pages.length === 0) return;

    const renderTasks = activeRenderTasks.current;

    // Cancel any existing render tasks
    renderTasks.forEach((task, pageNum) => {
      if (task && !task.cancelled) {
        console.log(`Cancelling existing render task for page ${pageNum}`);
        task.cancel();
      }
    });
    renderTasks.clear();

    const renderPages = async () => {
      try {
        // Wait longer for DOM to be fully ready
        await new Promise(resolve => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        });
        
        // Calculate scale once for all pages to ensure consistency
        const container = containerRef.current;
        let containerWidth = container ? container.clientWidth - 64 : 800; // More padding
        containerWidth = Math.max(containerWidth, 500); // Higher minimum
        
        // Get reference scale from first page
        const firstPage = pages[0];
        const referenceViewport = firstPage.getViewport({ scale: 1.0, rotation: 0 });
        const globalScale = Math.min(containerWidth / referenceViewport.width, 1.5); // Consistent max scale
        
        for (let i = 0; i < pages.length; i++) {
          const page = pages[i];
          let canvas = document.getElementById(`pdf-page-${i + 1}`);
          if (!canvas) {
            console.warn(`Canvas for page ${i + 1} not found, retrying...`);
            // Retry after a delay
            await new Promise(resolve => setTimeout(resolve, 100));
            canvas = document.getElementById(`pdf-page-${i + 1}`);
            if (!canvas) {
              console.error(`Canvas for page ${i + 1} still not found after retry`);
              continue;
            }
          }

          // Longer delay between pages for complex rendering
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }

          try {
            const context = canvas.getContext('2d', {
              alpha: false, // Disable alpha for better performance and consistent backgrounds
              willReadFrequently: false
            });
            
            // Check page's native rotation and other transformation info
            const pageInfo = page._pageInfo || page.pageInfo;
            const nativeRotation = pageInfo ? pageInfo.rotate || 0 : 0;
            
            // Get the page's view info for debugging transformations
            const view = pageInfo ? pageInfo.view : null;
            
            // Create viewport with standard PDF.js handling but force rotation to 0
            const viewport = page.getViewport({ 
              scale: globalScale,
              rotation: 0 // Override any embedded rotation
            });
            
            // Log detailed page info for debugging
            if (nativeRotation !== 0 || (view && (view[0] !== 0 || view[1] !== 0))) {
              console.log(`Page ${i + 1} - Native rotation: ${nativeRotation}°, View: [${view}], Viewport rotation: ${viewport.rotation}°`);
            }

            const canvasWidth = Math.floor(viewport.width);
            const canvasHeight = Math.floor(viewport.height);
            
            // Reset canvas completely
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            canvas.style.width = `${canvasWidth}px`;
            canvas.style.height = `${canvasHeight}px`;
            
            // Fill with white background explicitly
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvasWidth, canvasHeight);
            
            // Completely reset the canvas transformation matrix
            context.setTransform(1, 0, 0, 1, 0, 0);
            context.globalAlpha = 1.0;
            context.globalCompositeOperation = 'source-over';

            // Just log the transform for debugging but let PDF.js handle it normally
            const transform = viewport.transform;
            if (transform && nativeRotation !== 0) {
              console.log(`Page ${i + 1} viewport transform: [${transform.join(', ')}], native rotation: ${nativeRotation}°`);
            }

            // Cancel any existing render task for this canvas
            const pageNum = i + 1;
            const existingTask = activeRenderTasks.current.get(pageNum);
            if (existingTask && !existingTask.cancelled) {
              console.log(`Cancelling existing render task for page ${pageNum} before starting new one`);
              existingTask.cancel();
            }

            // Use standard PDF.js rendering
            const renderTask = page.render({
              canvasContext: context,
              viewport: viewport,
              intent: 'display',
              enableWebGL: false,
              renderInteractiveForms: false
            });

            // Track this render task
            renderTasks.set(pageNum, renderTask);

            try {
              await renderTask.promise;
              
              // Remove completed task from tracking
              renderTasks.delete(pageNum);
            } catch (renderError) {
              // Remove failed/cancelled task from tracking
              renderTasks.delete(pageNum);
              
              // Don't throw error for cancelled tasks
              if (renderError.name === 'RenderingCancelledException') {
                console.log(`Page ${pageNum} rendering was cancelled`);
                return; // Skip to next page
              } else {
                throw renderError; // Re-throw other errors
              }
            }
            
            // Verify the render completed successfully
            const imageData = context.getImageData(0, 0, 10, 10);
            const hasContent = imageData.data.some((pixel, index) => 
              index % 4 !== 3 && pixel !== 255 // Check for non-white, non-alpha pixels
            );
            
            if (!hasContent) {
              console.warn(`Page ${i + 1} appears to be blank after rendering`);
              // Don't retry to avoid canvas reuse error
            }

            console.log(`Page ${i + 1} rendered successfully (${canvasWidth}x${canvasHeight})`);
          } catch (renderError) {
            console.error(`Error rendering page ${i + 1}:`, renderError);
            // Try to render a fallback error message on the canvas
            const context = canvas.getContext('2d');
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.fillStyle = '#ff0000';
            context.font = '16px Arial';
            context.fillText(`Error loading page ${i + 1}`, 20, 40);
          }
        }
      } catch (err) {
        console.error('Error in renderPages:', err);
        setError('Error rendering PDF pages');
      }
    };

    renderPages();

    // Cleanup function to cancel renders when effect re-runs or component unmounts
    return () => {
      renderTasks.forEach((task, pageNum) => {
        if (task && !task.cancelled) {
          console.log(`Cleanup: Cancelling render task for page ${pageNum}`);
          task.cancel();
        }
      });
      renderTasks.clear();
    };
  }, [pages]);

  if (loading) {
    // Do not render a spinner here; parent handles loading overlay
    return null;
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'auto',
        bgcolor: 'background.paper'
      }}
    >
      {/* Enhanced multi-layer watermark overlay */}
      <Box
        ref={overlayRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 1000,
          // Main diagonal watermark pattern
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 120px,
                rgba(81, 104, 255, 0.025) 122px,
                rgba(81, 104, 255, 0.025) 124px
              ),
              repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 120px,
                rgba(81, 104, 255, 0.018) 122px,
                rgba(81, 104, 255, 0.018) 124px
              )
            `,
            opacity: 0.8,
            pointerEvents: 'none'
          },
          // Email watermark
          '&::before': {
            content: `"${user?.email}"`,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: '1.8rem',
            color: 'rgba(81, 104, 255, 0.12)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontWeight: 600,
            letterSpacing: '2px',
            textShadow: '0 0 10px rgba(81, 104, 255, 0.1)'
          }
        }}
      />
      
      {/* Corner watermarks for extra security */}
      <Box
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          pointerEvents: 'none',
          zIndex: 999,
          opacity: 0.6
        }}
      >
        <Box
          sx={{
            fontSize: '0.8rem',
            color: 'rgba(81, 104, 255, 0.32)',
            fontFamily: 'monospace',
            fontWeight: 600,
            transform: 'rotate(-15deg)',
            background: 'rgba(0, 0, 0, 0.02)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid rgba(81, 104, 255, 0.1)'
          }}
        >
          TopSWE © 2025
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          pointerEvents: 'none',
          zIndex: 999,
          opacity: 0.6
        }}
      >
        <Box
          sx={{
            fontSize: '0.8rem',
            color: 'rgba(81, 104, 255, 0.32)',
            fontFamily: 'monospace',
            fontWeight: 600,
            transform: 'rotate(15deg)',
            background: 'rgba(0, 0, 0, 0.02)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid rgba(81, 104, 255, 0.1)'
          }}
        >
          {user?.email?.split('@')[0] || 'User'}
        </Box>
      </Box>

      {/* Subtle repeating pattern watermarks */}
      {Array.from({ length: 12 }, (_, i) => (
        <Box
          key={i}
          sx={{
            position: 'fixed',
            top: `${15 + (i % 4) * 25}%`,
            left: `${10 + Math.floor(i / 4) * 30}%`,
            pointerEvents: 'none',
            zIndex: 998,
            opacity: 0.04,
            transform: `rotate(${-30 + (i % 3) * 15}deg)`,
            fontSize: '0.9rem',
            color: 'rgba(81, 104, 255, 0.22)',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontWeight: 500,
            userSelect: 'none'
          }}
        >
          TopSWE
        </Box>
      ))}

      {/* Center logo watermark */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '75%',
          transform: 'translate(-50%, -50%) rotate(-45deg)',
          pointerEvents: 'none',
          zIndex: 997,
          width: '200px',
          height: '200px',
          background: `url('/topswe-logo.png') center/contain no-repeat`,
          opacity: 0.08,
          filter: 'grayscale(1) contrast(0.8)',
          mixBlendMode: 'multiply'
        }}
      />
      
      {/* PDF pages */}
      {pages.map((_, index) => (
        <Box
          key={index}
          sx={{
            margin: '0 auto',
            padding: 2,
            '& canvas': {
              maxWidth: '100%',
              height: 'auto !important'
            }
          }}
        >
          <canvas id={`pdf-page-${index + 1}`} />
        </Box>
      ))}
    </Box>
  );
}

export default SecurePDFViewer; 
